import { useEffect, useMemo, useState } from 'react';
import CustomTable from '@shared/components/others/CustomTable/CustomTable';
import {
    getTestRunsBranchesFilterRequest,
    getTestRunsBuildsFilterRequest,
    getTestRunsRequest,
    getTestRunsReset,
} from '@pages/testruns/store';
import { useListState } from '@shared/hooks/useListState';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectGetTestRunsBranchesFilter,
    selectGetTestRunsBuildsFilter,
    selectGetTestRunsErrorInfo,
    selectGetTestRunsStatus,
    selectPatchTestRunStatus,
    selectTestRuns,
    selectTestRunsCount,
} from '@pages/testruns/store/selectors';
import { dateFormat, dateFormatWithoutHours, REQUEST_STATUS } from '@shared/constants/otherConstants';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import { Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { UI_FUNCTIONAL_TESTING_PATH, UI_STANDS_PATH, UI_TEST_RUN_PATH } from '@shared/constants/pathsConstants';
import dayjs from 'dayjs';
import TestrunResult from './TestrunResult';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import TestrunsTableToolbar from './TestrunsTableToolbar';
import { formatDuration, getObjectLinkById, toTitleCase } from '@shared/utils/utilsOld';
import { TESTPLAN_TESTRUN_STATUS } from '@pages/testruns/constants/testrunsConstants';
import { useSessionStorage } from 'usehooks-ts';
import { selectGetBranchesForDeployStatus, selectGetBuildsForDeployStatus } from '@pages/stands/store/selectors';
import TestResultChip from './TestResultChip';
import { useTestrunsChunk } from '@pages/testruns/hooks/useTestrunsChunk';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { AppLanguage } from '@shared/types';
import { useCreatedByUserColumn } from '@shared/hooks/useCreatedByUserColumn';
import TestrunStatusChip from './TestrunStatusChip';
import UtmsPagination from '@shared/components/ui/UtmsPagination';
import { getDefectLinkById } from '@shared/utils/utils';
import DefectsPopover from '@pages/defects/components/DefectsPopover';
import TestingEndDateCell from '@pages/testplans/components/TestingEndDateCell';
import JiraBugsCell from './JiraBugsCell';

const TestrunsTable = (props) => (
    <ComponentWithChunks
        featuresArray={['testRuns']}
        hooksArray={[useTestrunsChunk]}
        Component={TestrunsTableComponent}
        {...props}
    />
);

const TestrunsTableComponent = ({ defectId, customColumns }) => {
    const [selectedTestRunId, setSelectedTestRunId] = useState(null);
    const [createdAtLast, setCreatedAtLast] = useSessionStorage('createdAtLastTestRuns', 'month');
    const shortMode = Boolean(defectId);

    const {
        rowSelection,
        setRowSelection,
        pagination,
        setPagination,
        globalFilter,
        setGlobalFilter,
        showColumnFilters,
        setShowColumnFilters,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        fetchData,
    } = useListState('testRunsTable', false, getTestRunsRequest, {
        createdAtLast: shortMode ? undefined : createdAtLast,
        defectId,
        disableUrlParams: shortMode,
    });
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const testRuns = useSelector(selectTestRuns);
    const getTestRunsStatus = useSelector(selectGetTestRunsStatus);
    const testRunsCount = useSelector(selectTestRunsCount);
    const builds = useSelector(selectGetTestRunsBuildsFilter);
    const branches = useSelector(selectGetTestRunsBranchesFilter);
    const getBuildsStatus = useSelector(selectGetBuildsForDeployStatus);
    const getBranchesStatus = useSelector(selectGetBranchesForDeployStatus);
    const createdByUserColumn = useCreatedByUserColumn({ columnFilters, setColumnFilters, size: 150 });
    const dispatch = useDispatch();
    const patchTestRunStatus = useSelector(selectPatchTestRunStatus);
    const [defectsPopoverAnchorEl, setDefectsPopoverAnchorEl] = useState(null);
    const [defectsPopoverByTestrun, setDefectsPopoverByTestrun] = useState(null);

    const handleOpenDefectsPopover = (e, defects) => {
        e.stopPropagation();
        setDefectsPopoverAnchorEl(e.currentTarget);
        setDefectsPopoverByTestrun(defects);
    };

    useEffect(() => {
        return () => {
            dispatch(getTestRunsReset());
        };
    }, [dispatch]);

    useEffect(() => {
        for (let key in rowSelection) {
            if (rowSelection[key] === undefined) {
                continue;
            }

            setSelectedTestRunId(rowSelection[key] ? key : null);
        }
    }, [rowSelection]);

    useEffect(() => {
        if (patchTestRunStatus === REQUEST_STATUS.SUCCESS) {
            fetchData();
        }
    }, [fetchData, patchTestRunStatus]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'pk',
                header: 'ID',
                size: 70,
            },
            {
                accessorKey: 'title',
                header: t('test_run'),
                Cell: ({ cell }) => {
                    const testrun = cell.row.original;
                    const title = testrun.testplan?.title || testrun.testcase?.name;
                    const navigatePath = `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${testrun.pk}`;

                    return (
                        <OverflowTooltip title={title}>
                            <Link
                                underline="hover"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(navigatePath);
                                }}
                                href={navigatePath}
                                sx={{ cursor: 'pointer' }}
                            >
                                {title || `Testrun №${testrun.pk}`}
                            </Link>
                        </OverflowTooltip>
                    );
                },
                size: 140,
            },
            {
                accessorKey: 'createdAt',
                header: t('started'),
                Cell: ({ cell }) => {
                    const date = cell.getValue();
                    return date ? dayjs(date).format(dateFormat) : t('no_info');
                },
                filterVariant: 'date',
                size: 150,
            },
            {
                accessorKey: 'start_date',
                header: toTitleCase(t('start_date')),
                filterVariant: 'date',
                Cell: ({ cell }) => {
                    const date = cell.row.original.testplan?.start_date;
                    return date ? dayjs(date).format(dateFormatWithoutHours) : t('no_info');
                },
            },
            {
                accessorKey: 'results',
                header: t('result'),
                size: 340,
                Cell: ({ cell }) => {
                    const testrun = cell.row.original;
                    const isTestrunTestplan = Boolean(testrun.testplan);
                    const results = testrun.results ?? [];
                    const firstResult = results[0]?.result;

                    if (!isTestrunTestplan && !firstResult) {
                        return '–';
                    }

                    if (isTestrunTestplan && !firstResult) {
                        return (
                            <TestrunResult
                                skipAnimation
                                testrun={testrun}
                                width={i18n.language === AppLanguage.RU ? 420 : 300}
                            />
                        );
                    }

                    return <TestResultChip result={firstResult} />;
                },
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                accessorKey: 'status',
                header: t('status'),
                size: 130,
                Cell: ({ cell }) => <TestrunStatusChip status={cell.getValue()} />,
                filterVariant: 'select',
                filterSelectOptions: Object.values(TESTPLAN_TESTRUN_STATUS).map((item) => {
                    const value = item.toLowerCase();
                    return {
                        label: <TestrunStatusChip status={item} />,
                        value,
                    };
                }),
                enableSorting: false,
            },
            {
                accessorKey: 'end_date',
                header: toTitleCase(t('due_date')),
                filterVariant: 'date',
                Cell: ({ cell }) => <TestingEndDateCell date={cell.row.original.testplan?.end_date} />,
            },
            {
                accessorKey: 'type',
                header: t('type'),
                Cell: ({ cell }) => (cell.row.original.testplan ? t('test_plan') : t('test_case')),
                size: 110,
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: t('test_plan'), value: 'testplan' },
                    { label: t('test_case'), value: 'testcase' },
                ],
                enableSorting: false,
            },
            {
                accessorKey: 'branch',
                header: t('branch'),
                enableSorting: false,
                size: 150,
                filterVariant: 'autocomplete',
                filterSelectOptions: branches?.items,
                muiFilterAutocompleteProps: {
                    onOpen: () => dispatch(getTestRunsBranchesFilterRequest()),
                    loading: getBranchesStatus === REQUEST_STATUS.LOADING,
                    forcePopupIcon: true,
                    slotProps: {
                        popupIndicator: {
                            sx: {
                                display: 'flex !important',
                            },
                        },
                    },
                    sx: {
                        '.MuiAutocomplete-endAdornment': {
                            display: 'flex',
                        },
                    },
                },
                Cell: ({ cell }) => <OverflowTooltip title={cell.getValue()}>{cell.getValue()}</OverflowTooltip>,
            },
            {
                accessorKey: 'build',
                header: t('build'),
                size: 130,
                Cell: ({ cell }) => <OverflowTooltip title={cell.getValue()}>{cell.getValue()}</OverflowTooltip>,
                filterVariant: 'autocomplete',
                filterSelectOptions: builds?.items,
                muiFilterAutocompleteProps: {
                    onOpen: () => {
                        dispatch(getTestRunsBuildsFilterRequest());
                    },
                    loading: getBuildsStatus === REQUEST_STATUS.LOADING,
                    forcePopupIcon: true,
                    slotProps: {
                        popupIndicator: {
                            sx: {
                                display: 'flex !important',
                            },
                        },
                    },
                    sx: {
                        '.MuiAutocomplete-endAdornment': {
                            display: 'flex',
                        },
                    },
                },
            },
            {
                accessorKey: 'defects',
                header: t('defects'),
                size: 110,
                Cell: ({ cell }) => {
                    const testrun = cell.row.original;
                    const defects = cell.getValue();
                    const defect = defects[0];

                    if (!defects || !defects.length) {
                        return '–';
                    }

                    return (
                        <>
                            {defects.length > 1 ? (
                                <Link
                                    underline="hover"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={(e) => handleOpenDefectsPopover(e, testrun.defects)}
                                >
                                    {t('defectsWithCount', { count: defects.length })}
                                </Link>
                            ) : (
                                <OverflowTooltip title={defect.name}>
                                    <Link
                                        underline="hover"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(getDefectLinkById(defect.pk));
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                        href={getDefectLinkById(defect.pk)}
                                    >
                                        {defect.name}
                                    </Link>
                                </OverflowTooltip>
                            )}
                        </>
                    );
                },
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                accessorKey: 'jira_bugs',
                header: t('jira_bugs'),
                size: 125,
                enableColumnFilter: false,
                enableSorting: false,
                Cell: ({ cell }) => <JiraBugsCell jiraBugs={cell.getValue()} testRunResultId={cell.row.original.pk} />,
            },
            {
                accessorKey: 'stands',
                header: t('stands'),
                size: 90,
                Cell: ({ cell }) => {
                    const stands = cell.getValue();
                    const testrun = cell.row.original;

                    if (!stands.length) {
                        return '–';
                    }

                    return (
                        <>
                            <Link
                                underline="hover"
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(getObjectLinkById('testrun', testrun.pk, false, UI_STANDS_PATH));
                                }}
                                href={getObjectLinkById('testrun', testrun.pk, false, UI_STANDS_PATH)}
                            >
                                {t('stands', { count: stands.length })}
                            </Link>
                        </>
                    );
                },
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                accessorKey: 'duration',
                header: t('duration'),
                size: 135,
                Cell: ({ cell }) => formatDuration(cell.getValue()),
                enableColumnFilter: false,
            },
            createdByUserColumn,
        ],
        [
            builds?.items,
            branches?.items,
            getBranchesStatus,
            getBuildsStatus,
            dispatch,
            createdByUserColumn,
            i18n.language,
            navigate,
            t,
        ],
    );

    return (
        <>
            {getTestRunsStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={t('test_runs_r')}
                    action={t('fetching')}
                    resetActionCallback={getTestRunsReset}
                    errorSelector={selectGetTestRunsErrorInfo}
                />
            )}
            <CustomTable
                tableId="testruns-table"
                variant={defectId ? 'default' : 'outlined'}
                columns={
                    customColumns ? columns.filter((column) => customColumns.includes(column.accessorKey)) : columns
                }
                data={testRuns}
                state={{
                    showSkeletons: getTestRunsStatus === REQUEST_STATUS.LOADING,
                    density: 'compact',
                    showColumnFilters,
                    rowSelection,
                    globalFilter,
                    pagination,
                    sorting,
                    columnFilters,
                }}
                onRowSelectionChange={setRowSelection}
                onShowColumnFiltersChange={setShowColumnFilters}
                onGlobalFilterChange={setGlobalFilter}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                onColumnFiltersChange={setColumnFilters}
                rowCount={testRunsCount}
                toolbar={
                    shortMode
                        ? undefined
                        : (table) => (
                              <TestrunsTableToolbar
                                  table={table}
                                  selectedTestRunId={selectedTestRunId}
                                  createdAtLast={createdAtLast}
                                  setCreatedAtLast={setCreatedAtLast}
                                  fetchData={fetchData}
                              />
                          )
                }
                muiTableBodyRowProps={
                    shortMode
                        ? undefined
                        : ({ row }) => ({
                              onClick: () =>
                                  getTestRunsStatus === REQUEST_STATUS.SUCCESS
                                      ? setRowSelection((prev) => ({
                                            [row.id]: !prev[row.id],
                                        }))
                                      : undefined,
                              onDoubleClick: (e) => {
                                  e.preventDefault();
                                  navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${row.original.pk}`);
                              },
                              selected: rowSelection[row.id],
                          })
                }
                autoResetPageIndex={false}
                renderBottomToolbar={() => (
                    <UtmsPagination
                        view="table"
                        pagination={pagination}
                        count={testRunsCount}
                        handleChangePagination={setPagination}
                        sx={{
                            '& .MuiSelect-root': {
                                marginRight: 0,
                            },
                        }}
                    />
                )}
                {...(shortMode
                    ? {
                          muiTableContainerProps: {
                              sx: {
                                  maxHeight: 207,
                                  maxWidth: '100%',
                              },
                          },
                          muiTablePaperProps: {
                              sx: {
                                  minWidth: '100%',
                              },
                          },
                      }
                    : {})}
            />
            <DefectsPopover
                anchorEl={defectsPopoverAnchorEl}
                setAnchorEl={setDefectsPopoverAnchorEl}
                defects={defectsPopoverByTestrun}
            />
        </>
    );
};

export default TestrunsTable;
